import { Fragment, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import TrashIcon from "@heroicons/react/24/outline/TrashIcon";
import { useMutation } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import { Modal } from "@hsl/lgim-components/components";

import useTranslation, { Translate } from "../../../../../hooks/useTranslation";
import { SubscribableAssetsService } from "../../../../../openapi";
import { ExplorerStoreContext } from "../../../../../store/store";
import Button from "../../../../Button";
import { GenericErrorBoundary } from "../../../../GenericErrorBoundary/GenericErrorBoundary";
import Loading from "../../../../Loading";
import { EmptyState } from "../../../EmptyState";
import { type FundCentres } from "../../../useFundCentres";
import { useSearchParamString } from "../../ManageMyFunds";
import { FundRow } from "./FundRow";

interface Props {
    fundCentres: FundCentres;
}

export const MySubscriptionsAndWatchLists = ({ fundCentres }: Props) => {
    const { buttonClickEvent } = useYardActionEvents();
    const params = useSearchParamString();

    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const user = useFCSubscriptionStore((state) => state.user);
    const loadingUser = useFCSubscriptionStore((state) => state.loadingUser);
    const latestFC = useFCSubscriptionStore((state) => state.latestFC);
    const subscriptions = useFCSubscriptionStore(
        (state) => state.userSubscriptions,
    );
    const refreshSubscriptions = useFCSubscriptionStore(
        (state) => state.refreshUserSubscriptions,
    );
    const T = useTranslation();
    const [confirmRemove, setConfirmRemove] = useState<
        "funds" | "subscriptions" | null
    >(null);

    const subscribedFunds = Array.from(
        new Set(
            subscriptions
                .filter((s) => s.subscriptionStatus === "CONFIRMED")
                .map((s) => s.fundId),
        ),
    );

    const { mutate: deleteAll, isLoading: isDeleting } = useMutation({
        mutationFn: async (kind: "subscriptions" | "funds") => {
            let fundIds: string[] = [];
            switch (kind) {
                case "subscriptions":
                    fundIds = subscriptions
                        .filter((sub) => sub.subscriptionStatus === "CONFIRMED")
                        .filter((sub) => sub.subscriptionType === "FULL")
                        .map((sub) => sub.subscriptionId);
                    break;
                case "funds":
                    fundIds = subscriptions
                        .filter((sub) => sub.subscriptionStatus === "CONFIRMED")
                        .map((sub) => sub.subscriptionId);
                    break;
            }
            await SubscribableAssetsService.deleteSubscriptions(
                user!.userId,
                fundIds,
            );
            await refreshSubscriptions();
        },
    });

    if (loadingUser) {
        return <Loading />;
    }

    if (!user) {
        return <Navigate to={latestFC + params} />;
    }

    const subscribedFundsWithFundCentres = subscribedFunds.filter((fundId) => {
        const [type, id] = fundId.split("-") as [string, string];
        return fundCentres.some(
            (fc) => fc.data?.type === type && fc.data?.funds.includes(id),
        );
    });

    return (
        <GenericErrorBoundary>
            {fundCentres.some((fc) => !fc.data && fc.isLoading) ? (
                <Loading />
            ) : subscribedFundsWithFundCentres.length > 0 ? (
                <div className="mt-8 w-full">
                    <div className="flex flex-col justify-between gap-2 sm:flex-row sm:gap-4">
                        <p>
                            <Translate>
                                The following funds are in your subscriptions
                                and watchlist. You can edit subscriptions or
                                remove funds below.
                            </Translate>
                        </p>
                        <Popover className="relative">
                            <Popover.Button
                                className={cx(
                                    "flex items-center gap-1.5 transition-opacity",
                                    isDeleting &&
                                        "pointer-events-none opacity-50",
                                )}
                            >
                                <TrashIcon className="h-4 w-4" />
                                <span className="w-max underline">
                                    <Translate>Remove all funds</Translate>
                                </span>
                            </Popover.Button>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-200"
                                enterFrom="opacity-0 translate-y-1"
                                enterTo="opacity-100 translate-y-0"
                                leave="transition ease-in duration-150"
                                leaveFrom="opacity-100 translate-y-0"
                                leaveTo="opacity-0 translate-y-1"
                            >
                                <Popover.Panel className="absolute z-[55] mt-3 w-[calc(100vw-3rem)] max-w-xs transform max-sm:left-0 sm:right-0">
                                    <div className="overflow-hidden rounded bg-white p-6 shadow ring-1 ring-black/5">
                                        <Button
                                            color="white"
                                            variant="large"
                                            onClick={() => {
                                                buttonClickEvent({
                                                    name: "Remove all subscriptions",
                                                    position: "Unsubscribe",
                                                });
                                                setConfirmRemove(
                                                    "subscriptions",
                                                );
                                            }}
                                        >
                                            <Translate>
                                                Remove all subscriptions
                                            </Translate>
                                        </Button>
                                        <p className="mb-6 mt-2 text-sm">
                                            <Translate>
                                                Your funds will remain in your
                                                watchlist, but you will no
                                                longer receive email
                                                notification of updates to fund
                                                literature.
                                            </Translate>
                                        </p>
                                        <Button
                                            color="white"
                                            variant="large"
                                            onClick={() => {
                                                buttonClickEvent({
                                                    name: "Remove all funds",
                                                    position: "Unsubscribe",
                                                });
                                                setConfirmRemove("funds");
                                            }}
                                        >
                                            <Translate>
                                                Remove all funds
                                            </Translate>
                                        </Button>
                                        <p className="mt-2 text-sm">
                                            <Translate>
                                                Your funds will be removed from
                                                your watchlist and you will no
                                                longer receive email
                                                notification of updates to fund
                                                literature.
                                            </Translate>
                                        </p>
                                    </div>
                                </Popover.Panel>
                            </Transition>
                        </Popover>
                    </div>
                    <Modal
                        open={confirmRemove !== null}
                        onClose={() => setConfirmRemove(null)}
                        title={
                            T(
                                confirmRemove === "subscriptions"
                                    ? "Remove all subscriptions"
                                    : "Remove all funds",
                            ).result
                        }
                    >
                        <div className="p-8 text-left">
                            <p className="text-charcoal text-base">
                                {confirmRemove === "subscriptions" ? (
                                    <Translate>
                                        You are about to remove all of your
                                        subscriptions. Your funds will remain in
                                        your watchlist, but you will no longer
                                        receive email notification of updates to
                                        fund literature.
                                    </Translate>
                                ) : (
                                    <Translate>
                                        You are about to remove all of your
                                        funds. Your funds will be removed from
                                        your watchlist and you will no longer
                                        receive email notification of updates to
                                        fund literature.
                                    </Translate>
                                )}
                            </p>
                        </div>
                        <div className="flex gap-4 p-8">
                            <Button
                                onClick={() => {
                                    if (confirmRemove) {
                                        deleteAll(confirmRemove);
                                        setConfirmRemove(null);
                                    }
                                }}
                                color="primary"
                                variant="large"
                            >
                                <Translate>Remove</Translate>
                            </Button>
                            <Button
                                onClick={() => setConfirmRemove(null)}
                                color="white"
                                variant="large"
                            >
                                <Translate>Cancel</Translate>
                            </Button>
                        </div>
                    </Modal>
                    <GenericErrorBoundary>
                        <table className="mt-8 w-full table-auto">
                            <thead className="max-md:hidden">
                                <tr className="bg-platinum [&>th]:w-max [&>th]:px-5 [&>th]:py-2.5 [&>th]:font-medium">
                                    <th className="border-platinum border-l-[6px]">
                                        <Translate>Fund</Translate>
                                    </th>
                                    <th className="text-center">
                                        <Translate>Watchlist</Translate>
                                    </th>
                                    <th className="text-center">
                                        <Translate>Subscription</Translate>
                                    </th>
                                    <th className="text-right">
                                        <Translate>Date added</Translate>
                                    </th>
                                    <th className="sr-only">
                                        <Translate>Delete fund</Translate>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {subscribedFundsWithFundCentres
                                    .map((fundId) => {
                                        const [type, id] = fundId.split(
                                            "-",
                                        ) as [string, string];
                                        const query = fundCentres.find(
                                            (fc) =>
                                                fc.data?.type === type &&
                                                fc.data?.funds.includes(id),
                                        );
                                        return [type, id, query!] as const;
                                    })
                                    .filter(([, , query]) => !!query)
                                    .sort(
                                        ([, aId, aQuery], [, bId, bQuery]) => {
                                            try {
                                                const aFund = aQuery
                                                    .data!.store.getState()
                                                    .getFundById(`fund-${aId}`);
                                                const bFund = bQuery
                                                    .data!.store.getState()
                                                    .getFundById(`fund-${bId}`);
                                                if (!aFund) return -1;
                                                if (!bFund) return 1;
                                                return aFund.data.data.name.localeCompare(
                                                    bFund.data.data.name,
                                                );
                                            } catch (err) {
                                                console.error(err);
                                                return 0;
                                            }
                                        },
                                    )
                                    .map(([type, id, query]) => {
                                        return (
                                            <ExplorerStoreContext.Provider
                                                value={query.data!.store}
                                                key={`${type}-${id}`}
                                            >
                                                <FundRow
                                                    fundId={id}
                                                    expanded={
                                                        id === expandedRow
                                                    }
                                                    setExpanded={(value) => {
                                                        if (
                                                            !expandedRow ||
                                                            expandedRow === id
                                                        ) {
                                                            setExpandedRow(
                                                                value
                                                                    ? id
                                                                    : null,
                                                            );
                                                        }
                                                    }}
                                                    subscriptions={subscriptions.filter(
                                                        (x) =>
                                                            x.subscriptionStatus ===
                                                                "CONFIRMED" &&
                                                            x.fundId ===
                                                                `${type}-${id}`,
                                                    )}
                                                />
                                            </ExplorerStoreContext.Provider>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </GenericErrorBoundary>
                </div>
            ) : (
                <EmptyState className="mt-8" />
            )}
        </GenericErrorBoundary>
    );
};
